import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Modal, Divider, Button, CircularProgress } from "@mui/material";
import { MoreHoriz } from "@mui/icons-material";
import AddImg from "@assets/svg/addImg.svg?react";
import { usePropertySetting } from "../../../services/usePropertySetting";
import {
  ModalMainContainer,
  ModalContent,
  ModalCloseIcon,
  ModalUpperSection,
  ModalTextSection,
  ModalMainTitle,
  ModalDescription,
  ModalChangeButton,
  ModalLowerSection,
  ModalErrorText,
  ModalImageGrid,
  ModalImageGridItem,
  ModalGalleryContainerScroll,
  DividerStyled,
  DeleteMenuItemStyled,
  AddImageItemStyled,
  FileInputStyled,
  UploadButtonContainerStyled,
  TextError,
} from "../styles";
import ImageGridSkeleton from "./ImageGridSkeleton";
import LaodingWrapper from "../../../components/LoadingWrapper";
import RenderImage from "./RenderImage";
import AddIcon from "@mui/icons-material/Add";

interface PropertyImage {
  id: number;
  image: string;
  isPromotionalImage: boolean;
}

interface ImageGalleryModalProps {
  open: boolean;
  onClose: () => void;
  isRTL: boolean;
  propertyId?: string;
}

const ImageGalleryModal = ({
  open,
  onClose,
  isRTL,
  propertyId,
}: ImageGalleryModalProps) => {
  const { t } = useTranslation();
  const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [changingImageId, setChangingImageId] = useState<number | null>(null);
  const [isUploadingPromotionalImage, setIsUploadingPromotionalImage] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const changeImageFileInputRef = useRef<HTMLInputElement>(null);

  const {
    UploadPropertyImagesMutation,
    DeletePropertyImageMutation,
    GetPropertyImagesQuery,
  } = usePropertySetting();
  const {
    data: propertyImages,
    isLoading: propertyImagesLoading,
    refetch: refetchPropertyImages,
  } = GetPropertyImagesQuery(propertyId || "");
  const { mutate: uploadImages, isPending: isUploading } =
    UploadPropertyImagesMutation(propertyId || "");
  const { mutate: deleteImage, isPending: isDeleting } =
    DeletePropertyImageMutation();

  const handleDelete = async (imageId: string) => {
    setOpenMenuIndex(null);
    deleteImage(imageId, {
      onSuccess: () => {
        refetchPropertyImages();
      },
    });
  };

  const handleChangeImage = (imageId: number | undefined, isPromotional: boolean = false) => {
    // For promotional images, we can use a special ID or null since we're creating/replacing
    setChangingImageId(imageId || -1); // Use -1 as a special case for new promotional images
    setIsUploadingPromotionalImage(isPromotional);
    setOpenMenuIndex(null);
    changeImageFileInputRef.current?.click();
  };

  const handleChangeImageFileSelect = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      if (changingImageId !== null) {
        const formData = new FormData();
        Array.from(files).forEach((file) => {
          formData.append("images", file);
        });

        uploadImages({ formData, isPromotionalImage: isUploadingPromotionalImage }, {
          onSuccess: () => {
            refetchPropertyImages();
            
            // Only delete the old image if we're NOT uploading a promotional image AND we have a valid image ID
            // Promotional images should replace the existing promotional image automatically
            if (!isUploadingPromotionalImage && changingImageId !== -1) {
              deleteImage(changingImageId.toString(), {
                onSuccess: () => {
                  refetchPropertyImages();
                  setChangingImageId(null);
                  setIsUploadingPromotionalImage(false);
                  if (changeImageFileInputRef.current) {
                    changeImageFileInputRef.current.value = "";
                  }
                  setOpenMenuIndex(null);
                },
              });
            } else {
              // For promotional images or new uploads, just clean up the state and refetch
              setTimeout(() => {
                refetchPropertyImages();
              }, 500);
              setChangingImageId(null);
              setIsUploadingPromotionalImage(false);
              if (changeImageFileInputRef.current) {
                changeImageFileInputRef.current.value = "";
              }
              setOpenMenuIndex(null);
            }
          },
        });
      }
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      setSelectedFiles(files);
      setUploadError(null);
      setOpenMenuIndex(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFiles || selectedFiles.length === 0) {
      setUploadError(t("PROPERTIES_MANAGEMENT.SELECT_FILES_ERROR"));
      return;
    }
    if (selectedFiles.length > 10) {
      setUploadError(t("PROPERTIES_MANAGEMENT.MAX_IMAGES_ERROR"));
      return;
    }
    const formData = new FormData();
    Array.from(selectedFiles).forEach((file) => {
      formData.append("images", file);
    });

    uploadImages({ formData, isPromotionalImage: false }, {
      onSuccess: () => {
        refetchPropertyImages();
        setSelectedFiles(null);
      },
    });
  };

  // Find the promotional image
  const promotionalImage = propertyImages?.data?.result?.find((img: PropertyImage) => img.isPromotionalImage);

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <ModalMainContainer>
        <LaodingWrapper loading={isUploading || isDeleting}>
          <ModalContent>
            <ModalCloseIcon onClick={onClose} />
            {/* upper section  */}
            <ModalUpperSection>
              {/* text part  */}
              <ModalTextSection>
                <ModalMainTitle>
                  {t("PROPERTIES_MANAGEMENT.PROMOTIONAL_IMAGE")}
                </ModalMainTitle>
                <ModalDescription>
                  {t("PROPERTIES_MANAGEMENT.PROMOTIONAL_IMAGE_DESC")}
                </ModalDescription>
                <ModalChangeButton
                  onClick={() => {
                    handleChangeImage(promotionalImage?.id, true);
                  }}
                >
                  {t("PROPERTIES_MANAGEMENT.CHANGE_IMAGE")}
                </ModalChangeButton>
              </ModalTextSection>
              {/* image part  */}
              {promotionalImage?.image ? (
                <RenderImage
                  key={promotionalImage?.id}
                  src={promotionalImage?.image}
                  alt="property"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <div
                  className="flex items-center justify-center w-full h-[200px] rounded-md p-4
                           text-[#636e72] font-bold text-[25px] custom-dashed-bg"
                >
                  <span>{t("PROPERTIES_MANAGEMENT.NO_IMAGE_FOUND")}</span>
                </div>
              )}
            </ModalUpperSection>
            <DividerStyled />
            {/* lower section  */}
            <ModalLowerSection>
              <ModalMainTitle>
                {t("PROPERTIES_MANAGEMENT.ALL_IMAGES")}
              </ModalMainTitle>
              <ModalDescription>
                {t("PROPERTIES_MANAGEMENT.ADD_PROPERTY_IMAGES")}
              </ModalDescription>
              {/* gallery part  */}
              <ModalGalleryContainerScroll>
                <ModalImageGrid>
                  {!propertyImages?.data?.result ? (
                    <ImageGridSkeleton count={12} />
                  ) : (
                    <>
                      {propertyImages?.data?.result?.map(
                        (item: PropertyImage, index: number) => (
                          <ModalImageGridItem
                            isRTL={isRTL}
                            key={index}
                            isSelected={openMenuIndex === index}
                          >
                            <RenderImage
                              src={item?.image}
                              alt="property"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                            {/* Promotional Image Tag */}
                            {item?.isPromotionalImage && (
                              <div
                                style={{
                                  position: "absolute",
                                  top: "8px",
                                  ...(isRTL ? { right: "8px" } : { left: "8px" }),
                                  backgroundColor: "#5B3FFF",
                                  color: "white",
                                  padding: "4px 8px",
                                  borderRadius: "12px",
                                  fontSize: "10px",
                                  fontWeight: "600",
                                  zIndex: 2,
                                  textTransform: "uppercase",
                                }}
                              >
                                {t("PROPERTIES_MANAGEMENT.PROMOTIONAL")}
                              </div>
                            )}
                            <MoreHoriz
                              className="more-icon"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenMenuIndex(
                                  openMenuIndex === index ? null : index
                                );
                              }}
                            />
                            {openMenuIndex === index && (
                              <div className="image-menu">
                                <div
                                  className="menu-item"
                                  onClick={() => {
                                    handleChangeImage(item?.id);
                                  }}
                                >
                                  {t("PROPERTIES_MANAGEMENT.CHANGE_IMAGE")}
                                </div>
                                <DeleteMenuItemStyled
                                  isDeleting={isDeleting}
                                  className="menu-item delete"
                                  onClick={() =>
                                    handleDelete(item?.id?.toString() || "")
                                  }
                                >
                                  {isDeleting ? (
                                    <CircularProgress
                                      size={16}
                                      color="inherit"
                                    />
                                  ) : (
                                    t("GENERAL.DELETE")
                                  )}
                                </DeleteMenuItemStyled>
                              </div>
                            )}
                          </ModalImageGridItem>
                        )
                      )}
                      {/* <AddImageItemStyled
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <AddImg width={120} height={120} className="add-icon" />
                        <FileInputStyled
                          type="file"
                          ref={fileInputRef}
                          onChange={handleFileSelect}
                          accept="image/*"
                          multiple
                        />
                      </AddImageItemStyled> */}
                      <div
                        onClick={() => fileInputRef.current?.click()}
                        onChange={handleFileSelect}
                        style={{
                          backgroundColor: "#E6E0FF",
                        }}
                        className="flex flex-col items-center justify-center w-full h-[120px] rounded-md p-4
                           text-[#5B3FFF] font-[400] text-[12px] custom-dashed-no-image  cursor-pointer"
                      >
                        <FileInputStyled
                          type="file"
                          ref={fileInputRef}
                          onChange={handleFileSelect}
                          accept="image/*"
                          multiple
                        />
                        <AddIcon
                          sx={{
                            fontSize: 20,
                            color: "#5B3FFF",
                            backgroundColor: "#C1B2FF",
                            padding: "4px",
                            borderRadius: "8px",
                            marginBottom: "8px",
                          }}
                        />
                        <span>{t("PROPERTIES_MANAGEMENT.ADD_IMAGE")}</span>
                      </div>

                      {/* Hidden file input for change image functionality */}
                      <FileInputStyled
                        type="file"
                        ref={changeImageFileInputRef}
                        onChange={handleChangeImageFileSelect}
                        accept="image/*"
                        multiple={false}
                      />
                    </>
                  )}
                </ModalImageGrid>
              </ModalGalleryContainerScroll>
              {propertyImages?.data?.result?.length + selectedFiles?.length >
                11 && (
                <TextError>
                  {t(
                    "PROPERTIES_MANAGEMENT.You_have_reached_the_maximum_number_of_images"
                  )}
                </TextError>
              )}

              {selectedFiles && selectedFiles.length > 0 && (
                <UploadButtonContainerStyled>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleUpload}
                    disabled={
                      isUploading ||
                      propertyImages?.data?.result?.length +
                        selectedFiles?.length >
                        11
                    }
                    sx={{ minWidth: "120px" }}
                  >
                    {t("COMMON.UPLOAD")}
                  </Button>
                </UploadButtonContainerStyled>
              )}
            </ModalLowerSection>
          </ModalContent>
        </LaodingWrapper>
      </ModalMainContainer>
    </Modal>
  );
};

export default ImageGalleryModal;
